import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useBotContext } from './store/BotContext';
import { Button } from 'antd';
import { IoClose } from 'react-icons/io5';
import { formatBotPrice } from './search-results/formatBotPrice';
import { useBotCheckoutHandler } from './hooks/useBotCheckoutHandler';

const ICONS: Record<string, string> = { flight: '✈️', hotel: '🏨', package: '📦', activity: '🎯', event: '🎫' };

const CheckoutPanel: React.FC = () => {
  const { botState, botDispatch } = useBotContext();
  const { bot: t } = useSelector((s: RootState) => (s.config.translations));
  const handleBotCheckout = useBotCheckoutHandler();
  if (!botState.checkoutReady || !botState.checkoutPayload) return null;
  const p = botState.checkoutPayload;

  const handleBook = async () => {
    // Single web checkout path (decision #4): fetch the assembled V2 cart,
    // stash it under a checkout uuid, and land on /checkout/<uuid>. Same
    // handler the chat CTA uses — no more /shopping-cart for the web flow.
    await handleBotCheckout(p.session_id);
    botDispatch({ type: 'CLEAR_CHECKOUT' });
  };

  return (
    <div className="bot-checkout-overlay">
      <div className="bot-checkout">
        <div className="bot-checkout__header">
          <h3>{t?.checkout?.title}</h3>
          {/* Hide-only — keeps the payload in state so the header chip can
              re-open the panel instantly. CLEAR_CHECKOUT is reserved for
              full session resets. */}
          <button className="bot-icon-btn bot-icon-btn--dark" onClick={() => botDispatch({ type: 'HIDE_CHECKOUT' })}>
            <IoClose size={20} />
          </button>
        </div>
        <div className="bot-checkout__items">
          {p.itinerary_display.map(item => (
            <div key={item.selection_id} className="bot-checkout__item">
              <span className="bot-checkout__item-icon">{ICONS[item.product_type] ?? '📌'}</span>
              <div className="bot-checkout__item-info">
                <div className="bot-checkout__item-name">{item.display_name}</div>
                <div className="bot-checkout__item-summary">{item.display_summary}</div>
              </div>
              <div className="bot-checkout__item-price">{item.currency} {formatBotPrice(item.price)}</div>
            </div>
          ))}
        </div>
        <div className="bot-checkout__footer">
          <div className="bot-checkout__travellers">
            {t?.checkout?.travellers ?? 'Travellers'}: {p.travellers.adults} adults
            {p.travellers.children > 0 ? `, ${p.travellers.children} children` : ''}
            {p.travellers.infants > 0 ? `, ${p.travellers.infants} infants` : ''}
          </div>
          <div className="bot-checkout__total">
            <span>{t?.checkout?.total ?? 'Total'}</span>
            <strong>{p.currency} {formatBotPrice(p.total_estimated_price)}</strong>
          </div>
          <Button type="primary" block className="btn-style-5" onClick={handleBook}>
            {t?.checkout?.confirmBook ?? 'Confirm & Book'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPanel;
